import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ConfigProvider, Layout, Grid, Spin } from "antd";
import LeftNavBar from "./components/LeftNavBar";
import ManageCompanyLists from "./components/ManageCompanyLists";
import CompanyListDetails from "./components/CompanyListDetails";
import Login from "./components/Login";
import VendorCredits from "./components/VendorCredits";
import WorkspaceInformation from "./components/WorkspaceInformation";
import ManageUsers from "./components/ManageUsers";
import UserSSOLogin from "./components/UserSSOLogin";
import AuditLogs from "./components/AuditLogs";
import SignupBlacklist from "./components/SignupBlacklist";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme, SIDEBAR_WIDTH } from "./context/ThemeContext";
import DataCorrectionPage from "./components/DataCorrectionPage";
import DataCorrectionLogs from "./components/DataCorrectionLogs";
import PublicImages from "./components/PublicImages";
import WhatsNew from "./components/WhatsNew";
import CustomerStatistics from "./components/CustomerStatistics";
import AgentAnalytics from "./components/agentAnalytics";
import WorkflowTaskAnalytics from "./components/WorkflowTaskAnalytics";
import CreditAnalytics from "./components/CreditAnalytics";
import MeetingsAnalytics from "./components/MeetingsAnalytics";
import EmailMetrics from "./components/EmailMetrics";
import TrendsPipelineStatus from "./components/TrendsPipelineStatus";
import FeatureRequestSubmit from "./components/FeatureRequestSubmit";
import KnowledgeHub from "./components/KnowledgeHub";
import Pinboard from "./components/Pinboard";
import ProtectedRoute from "./components/ProtectedRoute";
import { PERMISSIONS } from "./utils/permissions";

const { Content } = Layout;
const { useBreakpoint } = Grid;

// Wrapper component for CompanyListDetails to get listId from URL params
function CompanyListDetailsWrapper() {
  const { listId } = useParams();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/manage-company-lists');
  };

  return <CompanyListDetails listId={listId} onBack={handleBack} />;
}

// Wrapper component for DataCorrectionLogs to get requestId from URL params
function DataCorrectionLogsWrapper() {
  const { requestId } = useParams();
  return <DataCorrectionLogs initialRequestId={requestId} />;
}

function AppContent() {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleViewLogs = (requestId) => {
    if (requestId) {
      navigate(`/data-correction-logs/${requestId}`);
    } else {
      navigate('/data-correction-logs');
    }
  };

  const handleNavigateToWorkspace = () => {
    navigate('/workspace-info');
  };

  // Get current active view from pathname
  const getActiveView = () => {
    const path = location.pathname;
    if (path === '/') return 'workspace-info';
    // Handle routes with parameters (e.g., /workspace-info/slug-name)
    if (path.startsWith('/workspace-info')) return 'workspace-info';
    if (path.startsWith('/manage-company-lists')) return 'manage-company-lists';
    if (path.startsWith('/data-correction-logs')) return 'data-correction-logs';
    if (path.startsWith('/workflow-task-analytics')) return 'workflow-task-analytics';
    if (path.startsWith('/credit-analytics')) return 'credit-analytics';
    if (path.startsWith('/meetings-analytics')) return 'meetings-analytics';
    if (path.startsWith('/email-metrics')) return 'email-metrics';
    if (path.startsWith('/feature-request')) return 'feature-request';
    if (path.startsWith('/knowledge-hub/pins')) return 'pinboard';
    if (path.startsWith('/knowledge-hub')) return 'knowledge-hub';
    return path.substring(1).split('/')[0]; // Get first segment after '/'
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: theme.background,
      }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: theme.background }}>
      <LeftNavBar
        activeView={getActiveView()}
        onViewChange={(view) => {
          if (view === "knowledge-hub") {
            navigate("/knowledge-hub");
          } else if (view === "pinboard") {
            navigate("/knowledge-hub/pins");
          } else {
            navigate(`/${view}`);
          }
        }}
        user={user}
        onLogout={logout}
        isMobile={isMobile}
        mobileOpen={mobileNavOpen}
        onMobileOpen={() => setMobileNavOpen(true)}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <Layout
        style={{
          marginLeft: isMobile ? 0 : SIDEBAR_WIDTH,
          paddingTop: isMobile ? 56 : 0,
          backgroundColor: theme.background,
          minHeight: '100vh',
          transition: 'margin-left 0.2s ease',
        }}
      >
        <Content
          style={{
            backgroundColor: theme.background,
            minHeight: isMobile ? 'calc(100vh - 56px)' : '100vh',
            maxWidth: '100%',
            overflowX: 'hidden',
            color: theme.text,
          }}
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/workspace-info" replace />} />
            <Route path="/workspace-info" element={<WorkspaceInformation />} />
            <Route path="/customer-statistics" element={<CustomerStatistics />} />
            <Route path="/workflow-task-analytics" element={<WorkflowTaskAnalytics />} />
            <Route path="/credit-analytics" element={<CreditAnalytics />} />
            <Route path="/meetings-analytics" element={<MeetingsAnalytics />} />
            <Route path="/email-metrics" element={<EmailMetrics />} />
            <Route path="/trends-pipeline" element={<TrendsPipelineStatus />} />
            <Route path="/vendor-credits" element={<VendorCredits />} />
            <Route path="/feature-request" element={<FeatureRequestSubmit />} />
            <Route path="/knowledge-hub" element={<KnowledgeHub />} />
            <Route path="/knowledge-hub/pins" element={<Pinboard />} />

            {/* Protected Routes with Permissions */}
            <Route
              path="/agent-analytics"
              element={
                <ProtectedRoute permission={PERMISSIONS.VIEW_AGENT_ANALYTICS}>
                  <AgentAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/user-sso-login"
              element={
                <ProtectedRoute permission={PERMISSIONS.USER_SSO}>
                  <UserSSOLogin onNavigateToWorkspace={handleNavigateToWorkspace} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/manage-company-lists"
              element={
                <ProtectedRoute permission={PERMISSIONS.MANAGE_COMPANY_LISTS}>
                  <ManageCompanyLists />
                </ProtectedRoute>
              }
            />

            <Route
              path="/manage-company-lists/:listId"
              element={
                <ProtectedRoute permission={PERMISSIONS.MANAGE_COMPANY_LISTS}>
                  <CompanyListDetailsWrapper />
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-correction"
              element={
                <ProtectedRoute permission={[PERMISSIONS.DATA_CORRECTION_UPLOADER, PERMISSIONS.DATA_CORRECTION_APPROVER]}>
                  <DataCorrectionPage onViewLogs={handleViewLogs} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-correction-logs/:requestId?"
              element={
                <ProtectedRoute permission={[PERMISSIONS.DATA_CORRECTION_UPLOADER, PERMISSIONS.DATA_CORRECTION_APPROVER]}>
                  <DataCorrectionLogsWrapper />
                </ProtectedRoute>
              }
            />

            <Route
              path="/public-images"
              element={
                <ProtectedRoute permission={PERMISSIONS.MANAGE_PUBLIC_IMAGES}>
                  <PublicImages />
                </ProtectedRoute>
              }
            />

            <Route
              path="/whats-new"
              element={
                <ProtectedRoute permission={PERMISSIONS.MANAGE_WHATS_NEW}>
                  <WhatsNew />
                </ProtectedRoute>
              }
            />

            <Route
              path="/manage-users"
              element={
                <ProtectedRoute permission={PERMISSIONS.MANAGE_USERS}>
                  <ManageUsers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/signup-blacklist"
              element={
                <ProtectedRoute permission={PERMISSIONS.MANAGE_SIGNUP_BLACKLIST}>
                  <SignupBlacklist />
                </ProtectedRoute>
              }
            />

            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute permission={PERMISSIONS.VIEW_AUDIT_LOGS}>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to workspace-info */}
            <Route path="*" element={<Navigate to="/workspace-info" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

function ThemedApp() {
  const { antdThemeConfig } = useTheme();

  return (
    <ConfigProvider theme={antdThemeConfig}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <ThemedApp />
      </ThemeProvider>
    </Router>
  );
}

export default App;
